// since this component is server side, we can't use hooks

import dynamic from "next/dynamic";

import { ToolTip, StadiumShippedTooltip } from "./client";

import Multimedia from "@sharedComponents/multimedia";
import StyledButton from "@stadium/ui/src/components/button/styled-button";
import ProductColorPills from "@sharedComponents/cards/product/color-pills";
import Pill from "@stadium/ui/src/components/pill";
import ProductPrice from "@components/shared/cards/product/price";
import CountryFlag from "@stadium/ui/src/shared/country-flag";

import { isByoProduct, isDSDProduct } from "@helpers/products/types";
import { getMinPriceVariant } from "@helpers/product-details";

import { CORE_ON_DEMAND_TEXT } from "@constants/products/listing";
import { WAREHOUSE_PRODUCT } from "@constants/products/detail";

import styles from "./product-tile.module.scss";

import ecofriendlyImg from "@stadium/ui/src/assets/images/eco-friendly.svg";
import coreOnDemandImg from "@stadium/ui/src/assets/images/core-on-demand.svg";
import sustainableImg from "@stadium/ui/src/assets/images/sustainable.svg";
import NoImage from "@assets/images/no-image-portrait.svg";
import transGifImg from "@assets/images/imageHolder.gif";
import embroideryImg from "@assets/images/svg/embroidery.svg";

const QuantitySelector = dynamic(() =>
  import("@components/shared/cards/product/quantity-selector"),
);

const ProductCountryListing = dynamic(() =>
  import("@stadium/shared/src/components/products/country-listing"),
);

const defaultFunction = () => {};

const ProductInfo = ({
  data,
  productQtyMapping = {},
  productImageClass = "",
  showColorDots,
  isPreviewCatalogMode,
  isSwagCreationShopFlow = false,
  isInCollection = false,
  handleAddRemoveToCollection = defaultFunction,
  automatedCatalogLoading = false,
  productTileClass = "",
  productTileFooterClass = "",
  productBrandClass = "",
  productNameClass = "",
  leftContentClass = "",
  productPriceClass = "",
  showInventoryStatus = false,
  productBottomRowClass = "",
  tileDataClass = "",
  isNextImage = false,
  height = "500",
  width = "500",
  unoptimized = false,
  imageLoadingStrategy,
  isLockerShop = true,
  isPricelessShop = false,
  showPrice = true,
  showMinQty = true,
  source,
  productQtyBlockClass,
  addToBox,
  removeFromBox,
  showAddToBox = false,
  country,
  isNewSwagmagicHost,
  isResponsiveView,
  disabled = false,
}) => {
  const {
    baseImage,
    variantImages,
    name,
    brands = [],
    brand,
    servingInfo = "",
    intent = "",
    ecoFriendly,
    type,
    sustainable,
    shippedFromStadium,
    colorOptions,
    colorOptionsCount,
    coreOnDemand,
    bulkAvailable: isBulkAvailable,
    minOrderableQuantity,
    locked,
    variants = [],
    storeProduct,
  } = data;

  const image =
    (showAddToBox && getMinPriceVariant(variants)?.baseImage) || baseImage || variantImages?.[0];

  const showCoreOnDemand = coreOnDemand && isLockerShop;

  const hasBadge =
    ecoFriendly ||
    sustainable ||
    shippedFromStadium ||
    showCoreOnDemand ||
    !!storeProduct?.embroidery;

  const minQty = isLockerShop || isBulkAvailable ? minOrderableQuantity : 0;

  const brandName = brand?.displayName || brands?.find((brand) => brand.name)?.name || "";

  const isWarehouseProduct = type === WAREHOUSE_PRODUCT;

  const renderProductInventoryStatus = () => {
    const { available, displayOosLabel, inLocker, inTransit } = productQtyMapping;
    const isProductInventorySyncing = !displayOosLabel && !inLocker && !inTransit;
    const isProductOutOfStock = !isProductInventorySyncing && available < 1;

    if (isProductInventorySyncing) {
      return (
        <Pill color="light-orange" className={styles.pillBtn}>
          Sync in progress
        </Pill>
      );
    }
    if (isProductOutOfStock) {
      return (
        <Pill color="pink" className={styles.pillBtn}>
          Out of stock
        </Pill>
      );
    }
    return (
      Boolean(inLocker || inTransit) && (
        <div className={`${styles.cutomizableWrapper} ${styles.qtyContainer}`}>
          <span>
            {inLocker || inTransit}&nbsp;in&nbsp;
            {inLocker ? "locker" : "transit"}
          </span>
        </div>
      )
    );
  };

  const qtySelectorProps = {
    data,
    productQtyBlockClass,
    source,
    addToBox,
    removeFromBox,
    disabled,
  };

  const renderAddRemoveToCollectionButton = () => {
    if (!isSwagCreationShopFlow) {
      return null;
    }

    return (
      <StyledButton
        styleType="no-style"
        onClick={handleAddRemoveToCollection}
        disabled={automatedCatalogLoading}
        className={styles.addToCollectionButton}
        aria-busy={automatedCatalogLoading}
        aria-label="Add to collection"
        aria-disabled={automatedCatalogLoading}
      >
        {automatedCatalogLoading ? (
          <span className={styles.addToCollectionButtonLoader} aria-hidden />
        ) : isInCollection ? (
          <span className={styles.minusIcon} />
        ) : (
          <span className={styles.addIcon} />
        )}
      </StyledButton>
    );
  };

  return (
    <div
      className={`${styles.productTile} ${productTileClass} ${
        isSwagCreationShopFlow ? styles.swagProductTile : ""
      }`}
      aria-hidden="true"
    >
      {isSwagCreationShopFlow && (
        <ProductCountryListing brands={brands} className={styles.countryListing} />
      )}
      <span className={styles.byoPill}>{isByoProduct(intent) && "BUILD-YOUR-OWN"}</span>
      {showInventoryStatus && isWarehouseProduct && renderProductInventoryStatus()}
      <div
        className={`${styles.productImageBlock} ${productImageClass} product-image-block ${
          isNewSwagmagicHost ? styles.productImageBlockV2 : ""
        }`}
      >
        {hasBadge && (
          <div className={styles.onDemandBadgeCont}>
            {!isLockerShop && sustainable && (
              <ToolTip
                containerClass={styles.toolTipWrapper}
                heading="Sustainable Printing"
                content="We only print once ordered, which reduces waste and preserves resources."
                isLightTheme
                disableOnResponsive
                placement="top-end"
                arrow={true}
              >
                <div id="sustainableIcon" className={styles.sutainableImgCont}>
                  <Multimedia
                    type="image"
                    src={sustainableImg}
                    height={16}
                    isNextImage={isNextImage}
                    className={styles.qualityImg}
                  />
                </div>
              </ToolTip>
            )}
            {storeProduct?.embroidery && (
              <ToolTip
                containerClass={`${styles.toolTipWrapper} ${styles.embroideryTipWrapper}`}
                heading="Embroidery"
                isLightTheme
                disableOnResponsive
                placement="top-end"
                arrow={true}
              >
                <div id="sustainableIcon" className={styles.sutainableImgCont}>
                  <Multimedia
                    type="image"
                    src={embroideryImg}
                    height={16}
                    isNextImage={isNextImage}
                    className={styles.qualityImg}
                  />
                </div>
              </ToolTip>
            )}
            {ecoFriendly && (
              <ToolTip
                containerClass={styles.toolTipWrapper}
                heading="Eco-friendly"
                content="This item is crafted from sustainable and recycled materials, reducing carbon footprint."
                disableOnResponsive
                isLightTheme
                placement="top-end"
                arrow={true}
              >
                <div id="ecoFriendlyIcon" className={styles.ecoFriendlyImgCont}>
                  <Multimedia
                    type="image"
                    src={ecofriendlyImg}
                    height={16}
                    isNextImage={isNextImage}
                    className={styles.qualityImg}
                  />
                </div>
              </ToolTip>
            )}
            {!!shippedFromStadium && <StadiumShippedTooltip />}
            {showCoreOnDemand && (
              <ToolTip
                containerClass={styles.toolTipWrapper}
                heading="Core On-Demand"
                content={CORE_ON_DEMAND_TEXT}
                disableOnResponsive
                isLightTheme
                placement="top-end"
                arrow={true}
              >
                <Multimedia
                  type="image"
                  src={coreOnDemandImg}
                  height={16}
                  isNextImage={isNextImage}
                  className={styles.qualityImg}
                />
              </ToolTip>
            )}
          </div>
        )}
        <img src={transGifImg} alt="" className={styles.gifImgHelper} />
        <Multimedia
          type="image"
          src={image || NoImage}
          alt={`${name} for`}
          height={height}
          width={width}
          isNextImage={isNextImage}
          lazyBoundary="400px"
          className={`${styles.cardTileImg} ${
            image ? styles.imageAvailable : styles.imageNotAvailable
          }`}
          unoptimized={unoptimized}
          loading={imageLoadingStrategy}
        />
        {renderAddRemoveToCollectionButton()}
        {showColorDots && (
          <ProductColorPills colors={colorOptions} totalColorsCount={colorOptionsCount} />
        )}
        {isDSDProduct(intent) && servingInfo && (
          <div className={styles.servePill}>SERVES {servingInfo}</div>
        )}
      </div>
      <div
        className={`${styles.tileFooter} ${productTileFooterClass} ${
          isNewSwagmagicHost ? styles.tileFooterV2 : ""
        } ${isSwagCreationShopFlow ? styles.swagTileFooter : ""}`}
      >
        <article
          className={`${styles.tileData} ${tileDataClass} ${
            isPreviewCatalogMode ? styles.tileDataContentCatalogMode : ""
          }`}
        >
          <div className={`${styles.leftContent} ${leftContentClass}`}>
            {brandName && (
              <div className={`${styles.brandName} ${productBrandClass}`}>{brandName}</div>
            )}
            <ul className={styles.productNameUl}>
              <li className={`${styles.productName} ${productNameClass}`}>{name}</li>
            </ul>
          </div>
          {country && !isSwagCreationShopFlow && (
            <CountryFlag
              key={country.iso}
              country={country}
              className={styles.imgFlag}
              type="round"
              width={67}
              height={50}
              isNextImage={isNextImage}
            />
          )}

          <div className={`${productBottomRowClass} ${styles.productBottomWrapper}`}>
            {showPrice && !isPricelessShop && (
              <ProductPrice
                product={data}
                source={source}
                intent={intent}
                productPriceClass={productPriceClass}
              />
            )}
            {minQty && showMinQty ? (
              <span className={styles.minQty}>
                <span aria-label="minimum">Min</span> {minQty}
              </span>
            ) : null}
            {showAddToBox && <QuantitySelector {...qtySelectorProps} />}
            {locked && (
              <div>
                <StyledButton styleType="icon-btn" className={styles.lockIconBtn}>
                  <i className={styles.lockIcon} />
                </StyledButton>
              </div>
            )}
          </div>
        </article>

        {/* Visible in mobile only */}
        {country && brandName && (
          <div className={`${styles.productName} ${styles.productNameMobile}`}>{name}</div>
        )}

        {/* for responsive views only */}
        {isResponsiveView && isNewSwagmagicHost && showColorDots && (
          <div className={styles.colorListNewResponsive}>
            <ProductColorPills colors={colorOptions} totalColorsCount={colorOptionsCount} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductInfo;
